import { forwardRef, useEffect, useState } from 'react';
import { useApi } from '@/lib/legacy';
import { notifyError, notifySuccess } from '@/lib/notify';
import { uniqueId } from '../../../ui/utils';
import { usePolicyPageContext as usePolicyPageContextJs } from '../context/PolicyPageContext';
import { ActionButton, ActionRow, Grid, type Row } from '../tabParts';
import { LenderLossPayee } from './LenderLossPayee';

type PayeeRow = { id: string; lossPayee: string };
type PolicyPageState = { lossPayee: PayeeRow[]; setLossPayee: (update: PayeeRow[] | ((prev: PayeeRow[]) => PayeeRow[])) => void };
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: pages/PolicyPage/context/PolicyPageContext.jsx
const usePolicyPageContext = usePolicyPageContextJs as any as () => PolicyPageState;

/** The policy's loss payees (add, edit, save), and its lender loss payee below. */
export const LossPayee = forwardRef<
  unknown,
  { policy: Row; setGridReference: (ref: unknown) => void; onSelectionChanged: (context: unknown) => void }
>(({ policy, setGridReference, onSelectionChanged }) => {
  const { createEndorsement } = useApi();
  const { lossPayee, setLossPayee } = usePolicyPageContext();
  const [isLoading, setIsLoading] = useState(false);
  const [lenderLossPayeeRef, setLenderLossPayeeRef] = useState<unknown>();

  const lossPayeeColumn = [
    {
      headerName: 'Loss Payee',
      field: 'lossPayee',
      editable: true,
      headerCheckboxSelection: true,
      checkboxSelection: true,
      valueSetter: (params: { newValue: string; data: PayeeRow }) => {
        const { newValue, data } = params;
        data.lossPayee = newValue;
        setLossPayee((prevData) => prevData.map((row) => (row.id === data.id ? { ...row, lossPayee: newValue } : row)));
        return true;
      },
    },
  ];

  useEffect(() => {
    if (policy.lossPayee && policy.lossPayee?.length) {
      setLossPayee(policy.lossPayee?.map((payee: string) => ({ id: uniqueId(), lossPayee: payee })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policy.lossPayee]);

  const handleAddRow = () => {
    const newRow = { id: uniqueId(), lossPayee: '' };
    setLossPayee((prevData) => (Array.isArray(prevData) ? [...prevData, newRow] : [newRow]));
  };

  const payload = {
    endorsementType: 'lossPayee',
    endorsementData: {
      lossPayee: lossPayee.map(({ lossPayee: payee }) => payee).filter((value) => value && value.trim() !== ''),
    },
  };

  const handleOnSave = async () => {
    setIsLoading(true);
    try {
      await createEndorsement(policy.id, payload);
      notifySuccess('Loss Payee Updated Successfully.');
    } catch (error) {
      console.error('Error updating loss payee:', error);
      notifyError('An error occurred while updating loss payee. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div className="max-h-[600px] overflow-y-auto">
        <Grid
          data={lossPayee}
          columnDefs={lossPayeeColumn}
          setGridReference={setGridReference}
          rowSelection="multiple"
          shouldShowPagination={false}
          columnWidth={150}
          floatingFilter={false}
          onSelectionChanged={onSelectionChanged}
        />
      </div>
      <ActionRow>
        <ActionButton onClick={handleAddRow}>Add Payee</ActionButton>
        <ActionButton onClick={handleOnSave} loading={isLoading}>
          Save
        </ActionButton>
      </ActionRow>
      <LenderLossPayee policy={policy} setLenderLossPayeeRef={setLenderLossPayeeRef} lenderLossPayeeRef={lenderLossPayeeRef} />
    </div>
  );
});
LossPayee.displayName = 'LossPayee';
